import { SearchInput } from "@/components/flexkit/SearchInput";
import { WorkflowStatusFilter, WorkflowStatusFilterValue } from "@/components/automations/AutomationStatusFilter";
import {
    AutomationCreatorFilter,
    ALL_CREATORS_VALUE,
    type CreatorFilterValue,
} from "@/components/automations/AutomationCreatorFilter";
import { useEffect, useRef, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import { type AutomationFilters } from "@/components/automations/automation-filters";
import { cn } from "@/components/podkit/lib/cn";

type AutomationListFiltersProps = {
    onFiltersChange: (filters: AutomationFilters) => void;
};

export const AutomationListFilters: FC<AutomationListFiltersProps> = ({ onFiltersChange }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
    const [statusFilter, setStatusFilter] = useState<WorkflowStatusFilterValue>(WorkflowStatusFilterValue.All);
    const [creatorFilter, setCreatorFilter] = useState<CreatorFilterValue>(ALL_CREATORS_VALUE);

    // Emit filter changes when debounced search changes
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        onFiltersChange({
            search: debouncedSearchQuery.trim(),
            status: statusFilter,
            creator: creatorFilter,
        });
    }, [debouncedSearchQuery, onFiltersChange, statusFilter, creatorFilter]);

    return (
        <div
            className={cn(
                "flex flex-col gap-2",
                // 512px = (min width of search + gap) + (2x width of selectors + gap))
                // If we have >= 512px then we have enough space to show everything in a single row
                "@[512px]:flex-row @[512px]:items-center",
            )}
        >
            <SearchInput
                placeholder="Search automations"
                wrapperClassName="flex-1 min-w-44"
                className="max-w-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery("")}
                data-testid="automation-search-input"
            />
            <div
                className={cn(
                    // Use a single column when there isn't enough space
                    "grid grid-cols-1 items-stretch gap-2",
                    "[&>button]:min-w-32",
                    // 264px = 2 x (min width of filters) + gap
                    // If we have >= 264px then we can show the filters next to each other taking up 50% of the space
                    "@[264px]:grid-cols-2",
                    // Only fixed width for buttons when everything is in one row
                    "[&>button]:@[512px]:w-40",
                )}
            >
                <AutomationCreatorFilter value={creatorFilter} onChange={setCreatorFilter} />
                <WorkflowStatusFilter value={statusFilter} onChange={setStatusFilter} />
            </div>
        </div>
    );
};
